<template>
    <v-row no-gutters class="d-block mt-3">
        <v-col cols="12">
            <v-card>
                <v-card-text>
                    <v-form ref="form" v-model="valid" enctype="multipart/form-data">
                        <v-row>
                            <!-- Datos de la firma -->
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="firmaModel.numero_identidad"
                                    label="Número de identidad"
                                    placeholder="0801199912345"
                                    :rules="[...requerido, v => !v || /^[0-9A-Za-z-]{5,20}$/.test(String(v).trim()) || 'Solo dígitos o letras, sin espacios']"
                                    maxlength="20"
                                    prepend-icon="badge"
                                    hint="Identificador único de quien firma."
                                    persistent-hint
                                    class="mb-2"
                                />
                                <v-text-field
                                    v-model="firmaModel.nombre"
                                    label="Nombres"
                                    placeholder="Ej. Carlos Francisco"
                                    :rules="requerido"
                                    maxlength="64"
                                    prepend-icon="person"
                                />
                                <v-text-field
                                    v-model="firmaModel.apellido"
                                    label="Apellidos"
                                    placeholder="Ej. Suazo Calderón"
                                    :rules="requerido"
                                    maxlength="64"
                                    prepend-icon="person_outline"
                                />

                                <v-select
                                    v-model="firmaModel.categoria"
                                    :items="categorias"
                                    item-text="texto"
                                    item-value="valor"
                                    label="Categoría"
                                    :rules="requerido"
                                    prepend-icon="workspace_premium"
                                    :hint="ayudaCategoria"
                                    persistent-hint
                                />

                                <v-autocomplete
                                    class="mt-3"
                                    v-model="firmaModel.departamento"
                                    :items="departamentosOpciones"
                                    label="Departamento"
                                    placeholder="Elija el departamento"
                                    :rules="requerido"
                                    prepend-icon="corporate_fare"
                                    hint="Los departamentos se administran en Administración → Departamentos."
                                    persistent-hint
                                />

                                <v-select
                                    class="mt-3"
                                    v-model="firmaModel.sede_id"
                                    :items="sedes"
                                    item-text="nombre"
                                    item-value="sede_id"
                                    label="Sede"
                                    clearable
                                    prepend-icon="location_city"
                                    hint="Las sedes se administran en Administración → Sedes."
                                    persistent-hint
                                />

                                <v-expansion-panels flat class="mt-3 convenios">
                                    <v-expansion-panel>
                                        <v-expansion-panel-header class="px-0 py-1 text-body-2">
                                            <span><v-icon small left>handshake</v-icon> Convenios <span class="grey--text">(opcional{{ conveniosLlenos ? `, ${conveniosLlenos}` : '' }})</span></span>
                                        </v-expansion-panel-header>
                                        <v-expansion-panel-content class="px-0">
                                            <v-text-field v-for="n in 4" :key="n" v-model="firmaModel['convenio_' + n]" :label="'Convenio ' + n"
                                                          maxlength="200" dense prepend-icon="handshake"/>
                                        </v-expansion-panel-content>
                                    </v-expansion-panel>
                                </v-expansion-panels>

                                <div class="text-caption grey--text text--darken-1 ml-8 mt-2">
                                    <template v-if="esEdicion">
                                        Registrada el <b>{{ firmaModel.fecha_registro | fechaESP }}</b>
                                        <span v-if="firmaModel.registrado_por">por <b>{{ firmaModel.registrado_por }}</b></span>
                                        <span v-if="firmaModel.fecha_actualiza"> · última modificación {{ firmaModel.fecha_actualiza | fechaESPHora }}</span>
                                    </template>
                                    <template v-else>La fecha de registro la pone el sistema al guardar.</template>
                                </div>

                                <v-switch
                                    v-show="esEdicion"
                                    v-model="firmaModel.activo"
                                    :label="firmaModel.activo ? 'Firma activa' : 'Firma inactiva'"
                                    color="success" class="font-weight-bold" inset hide-details
                                ></v-switch>
                            </v-col>

                            <!-- Imagen y vista previa -->
                            <v-col cols="12" md="6">
                                <v-file-input
                                    outlined dense
                                    truncate-length="40"
                                    v-model="archivo"
                                    :label="esEdicion ? 'Reemplazar imagen de la firma (PNG)' : 'Imagen de la firma (PNG)'"
                                    :rules="reglasArchivo"
                                    accept="image/png"
                                    prepend-icon="approval"
                                    show-size
                                    hint="PNG con fondo transparente, recortada al trazo. Máximo 5 MB."
                                    persistent-hint
                                    @change="archivoElegido"
                                />

                                <div class="vista mt-4">
                                    <div class="vista__titulo">
                                        <span><v-icon small>visibility</v-icon> Vista rápida</span>
                                        <v-btn-toggle v-model="fondo" mandatory dense>
                                            <v-btn x-small value="papel" title="Sobre papel blanco">Papel</v-btn>
                                            <v-btn x-small value="cuadros" title="Cuadros: deja ver si el fondo es transparente">Transparencia</v-btn>
                                        </v-btn-toggle>
                                    </div>

                                    <div class="vista__hoja" :class="'vista__hoja--' + fondo">
                                        <div class="vista__firma">
                                            <img v-if="imagen" :src="imagen" alt="Firma" @load="analizarImagen">
                                            <span v-else class="grey--text text-caption">Seleccione la imagen para verla aquí</span>
                                        </div>
                                        <div class="vista__linea"></div>
                                        <div class="vista__nombre">{{ nombreCompleto || 'Nombre de quien firma' }}</div>
                                        <div class="vista__cargo">{{ cargo }}</div>
                                    </div>

                                    <div class="text-caption grey--text text--darken-1 mt-1" v-if="dimensiones">
                                        {{ dimensiones.ancho }} × {{ dimensiones.alto }} px
                                    </div>
                                    <v-alert dense text type="warning" class="mt-2 mb-0" v-if="sinTransparencia">
                                        Esta imagen no tiene fondo transparente: en el certificado se verá un recuadro
                                        blanco tapando la línea y el fondo. Quite el fondo antes de subirla.
                                    </v-alert>
                                    <v-alert dense text type="warning" class="mt-2 mb-0" v-else-if="muyPequena">
                                        La imagen es pequeña y puede verse borrosa al imprimir. Se recomienda al menos 600 px de ancho.
                                    </v-alert>
                                </div>

                                <!-- Vista sobre una plantilla real -->
                                <div class="mt-4">
                                    <v-alert dense text type="info" class="mb-2" v-if="firmaModel.categoria && !plantillasCategoria.length">
                                        Ninguna plantilla activa tiene espacio para la firma de
                                        <b>{{ textoCategoria.toLowerCase() }}</b> (cuadro <code>{{ cuadro }}</code>), así que hoy
                                        esta firma no se imprime en los certificados. Se agrega en la plantilla PowerPoint.
                                    </v-alert>
                                    <v-row dense align="center" v-else>
                                        <v-col cols="12" sm="7">
                                            <v-select
                                                v-model="plantillaId"
                                                :items="plantillasCategoria"
                                                item-text="descripcion"
                                                item-value="plantilla_certificado_id"
                                                label="Ver sobre la plantilla"
                                                outlined dense hide-details
                                                :disabled="!firmaModel.categoria"
                                            />
                                        </v-col>
                                        <v-col cols="12" sm="5">
                                            <v-btn block small color="primary" outlined :loading="generando"
                                                   :disabled="!puedeVerDocumento" @click="verEnCertificado">
                                                <v-icon small left>description</v-icon> Ver en el certificado
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </div>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-divider class="mt-1"></v-divider>
                <v-card-actions class="justify-center py-3">
                    <v-btn outlined small @click="regresar()">
                        <v-icon>arrow_back</v-icon>
                        Regresar
                    </v-btn>
                    <v-btn outlined small color="warning" @click="reset()">
                        <v-icon>restart_alt</v-icon>
                        Limpiar
                    </v-btn>
                    <v-btn color="primary" small :loading="loading" :disabled="loading" @click="firmaSave()">
                        <v-icon>save</v-icon>
                        Guardar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-col>

        <!-- Certificado de ejemplo con la firma colocada -->
        <v-dialog v-model="dialogoDocumento" max-width="1100">
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-bold">
                    <v-icon left color="primary">description</v-icon>
                    Vista previa en el certificado
                    <v-spacer></v-spacer>
                    <v-btn icon small @click="dialogoDocumento = false"><v-icon>close</v-icon></v-btn>
                </v-card-title>
                <v-card-text>
                    <p class="text-caption grey--text text--darken-1 mb-2">
                        Certificado de ejemplo con datos ficticios. Solo se coloca esta firma, en el espacio de
                        <b>{{ textoCategoria.toLowerCase() }}</b>; los demás espacios de firma se dejan vacíos.
                    </p>
                    <img v-if="documento" :src="documento" alt="Certificado de ejemplo con la firma" class="documento">
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
const CUADROS = {instructor: "firma_tutor", jefe_departamento: "firma_jefe_depto", director: "firma_dir_ejec", encargado: "firma_jefe_depto"};

export default {
    name: "AdministracionFirmaFormulario",
    props: {
        getParams: {
            type: Object,
            required: false,
        }
    },
    data() {
        return {
            valid: true,
            loading: false,
            generando: false,
            categorias: [],
            departamentos: [],
            sedes: [],
            plantillas: [],
            plantillaId: null,
            archivo: null,
            imagen: null,
            imagenLocal: null,
            fondo: "papel",
            dimensiones: null,
            sinTransparencia: false,
            documento: null,
            dialogoDocumento: false,
            hoy: new Date().toISOString().substr(0, 10),
            requerido: [(v) => !!(v && String(v).trim()) || 'Campo requerido'],
            firmaModel: {
                persona_id: null,
                numero_identidad: null,
                nombre: null,
                apellido: null,
                categoria: null,
                departamento: null,
                sede_id: null,
                convenio_1: null,
                convenio_2: null,
                convenio_3: null,
                convenio_4: null,
                fecha_registro: null,
                activo: true,
            },
        }
    },
    computed: {
        departamentosOpciones() {
            const actual = this.firmaModel.departamento;
            return actual && !this.departamentos.includes(actual) ? [actual, ...this.departamentos] : this.departamentos;
        },
        esEdicion() {
            return !!Number(this.firmaModel.persona_id);
        },
        conveniosLlenos() {
            return [1, 2, 3, 4].filter(n => this.firmaModel["convenio_" + n]).length;
        },
        reglasArchivo() {
            return [
                (v) => this.esEdicion || !!v || 'Seleccione la imagen de la firma',
                (v) => !v || v.type === 'image/png' || /\.png$/i.test(v.name) || 'La firma debe ser una imagen PNG',
                (v) => !v || v.size <= 5 * 1024 * 1024 || 'La imagen no puede superar 5 MB',
            ];
        },
        nombreCompleto() {
            return [this.firmaModel.nombre, this.firmaModel.apellido].filter(Boolean).join(" ").trim();
        },
        textoCategoria() {
            const categoria = this.categorias.find(c => c.valor === this.firmaModel.categoria);
            return categoria ? categoria.texto : "";
        },
        cargo() {
            return [this.textoCategoria, this.firmaModel.departamento].filter(Boolean).join(" · ") || "Categoría · Departamento";
        },
        cuadro() {
            return CUADROS[this.firmaModel.categoria] || "";
        },
        ayudaCategoria() {
            if (this.firmaModel.categoria === "instructor") {
                return "La firma del instructor se enlaza por nombre: Nombres + Apellidos debe ser igual a la columna tutor del Excel.";
            }
            return "Define en qué espacio del certificado se coloca la firma.";
        },
        // Plantillas que tienen el cuadro de firma de la categoría elegida.
        plantillasCategoria() {
            return this.plantillas.filter(p => p.categorias.includes(this.firmaModel.categoria));
        },
        muyPequena() {
            return !!this.dimensiones && this.dimensiones.ancho < 600;
        },
        puedeVerDocumento() {
            return !!this.firmaModel.categoria && !!this.plantillaId && (!!this.archivo || this.esEdicion);
        },
    },
    watch: {
        plantillasCategoria(lista) {
            if (!lista.find(p => p.plantilla_certificado_id === this.plantillaId)) {
                this.plantillaId = lista.length ? lista[0].plantilla_certificado_id : null;
            }
        },
    },
    methods: {
        regresar() {
            this.$router.go(-1)
        },
        reset() {
            this.archivo = null;
            this.liberarImagenLocal();

            if (this.esEdicion) {
                this.getFirmaEdit();
            } else {
                this.$refs.form.reset();
                this.imagen = null;
                this.dimensiones = null;
                this.sinTransparencia = false;
                this.$nextTick(() => {
                    this.firmaModel.activo = true;
                });
            }
        },
        goToEdit() {
            let token = window.btoa(this.firmaModel.persona_id);
            this.$router.push({name: 'AdministracionFirmasEditar', query: {token}});
        },
        getFirmaCreate() {
            this.loading = true;

            window.axios.get('/personas-administrativas/create', {}).then(response => {
                if (response.data.success) {
                    const datos = response.data.data;
                    this.categorias = datos.categorias || [];
                    this.departamentos = datos.departamentos || [];
                    this.sedes = datos.sedes || [];
                    this.plantillas = datos.plantillas || [];
                    this.getFirmaEdit();
                } else if (response.data.success === false) {
                    this.$toast.error(response.data.message);
                }
            })
                .catch(error => {
                    console.log(error)
                    this.$toast.error("No se pudo obtener los datos para continuar.");
                })
                .finally(() => {
                    this.loading = false;
                })
        },
        getFirmaEdit() {
            if (!Number(this.getParams.persona_id)) return;

            this.loading = true;
            let params = {persona_id: this.getParams.persona_id}

            window.axios.get('/personas-administrativas/edit', {params}).then(response => {
                if (response.data.success) {
                    if (response.data.data.length) {
                        const datos = response.data.data[0];
                        datos.fecha_registro = (datos.fecha_registro || "").substr(0, 10) || null;
                        this.firmaModel = datos;
                        // La imagen está en almacenamiento privado: se muestra con su enlace firmado.
                        this.imagen = datos.firma_url;
                    } else {
                        this.$toast.error("No se encontraron los datos de la firma.");
                    }
                } else if (response.data.success === false) {
                    this.$toast.error(response.data.message);
                }
            })
                .catch(error => {
                    console.log(error)
                    this.$toast.error("No se pudo obtener la información de la firma.");
                })
                .finally(() => {
                    this.loading = false;
                })
        },
        datosFormulario() {
            let formData = new FormData;
            formData.append("persona_id", this.firmaModel.persona_id || "");
            formData.append("numero_identidad", (this.firmaModel.numero_identidad || "").trim());
            formData.append("nombre", (this.firmaModel.nombre || "").trim());
            formData.append("apellido", (this.firmaModel.apellido || "").trim());
            formData.append("categoria", this.firmaModel.categoria || "");
            formData.append("departamento", (this.firmaModel.departamento || "").trim());
            if (this.firmaModel.sede_id) formData.append("sede_id", this.firmaModel.sede_id);
            [1, 2, 3, 4].forEach(n => formData.append("convenio_" + n, (this.firmaModel["convenio_" + n] || "").trim()));
            if (this.archivo) formData.append("firma", this.archivo);
            return formData;
        },
        firmaSave() {
            if (!this.$refs.form.validate()) {
                this.$toast.warning("Complete toda la información requerida.");
                return;
            }

            this.loading = true;
            let url = this.esEdicion ? '/personas-administrativas/update' : '/personas-administrativas/store';
            const formData = this.datosFormulario();
            formData.append("activo", this.firmaModel.activo ? 1 : 0);

            window.axios.post(url, formData).then(response => {
                if (response.data.success) {
                    this.$toast.success(response.data.message);

                    if (this.esEdicion) {
                        this.archivo = null;
                        this.liberarImagenLocal();
                        this.getFirmaEdit();
                    } else {
                        this.firmaModel.persona_id = response.data.data;
                        this.goToEdit();
                    }
                } else if (response.data.success === false) {
                    this.$toast.error(response.data.message);
                }
            })
                .catch(error => this.mostrarError(error, "No se pudo guardar."))
                .finally(() => {
                    this.loading = false;
                })
        },
        // ----- Vista rápida -----
        archivoElegido(file) {
            this.liberarImagenLocal();
            this.dimensiones = null;
            this.sinTransparencia = false;

            if (file) {
                this.imagenLocal = URL.createObjectURL(file);
                this.imagen = this.imagenLocal;
            } else {
                this.imagen = this.esEdicion ? this.firmaModel.firma_url : null;
            }
        },
        liberarImagenLocal() {
            if (this.imagenLocal) {
                URL.revokeObjectURL(this.imagenLocal);
                this.imagenLocal = null;
            }
        },
        // Tamaño y transparencia. Solo se puede leer el contenido de la imagen recién
        // elegida; la que viene del servidor es de otro origen y el navegador lo impide.
        analizarImagen(evento) {
            const img = evento.target;
            this.dimensiones = {ancho: img.naturalWidth, alto: img.naturalHeight};
            this.sinTransparencia = false;

            if (!this.imagenLocal) return;

            try {
                const escala = Math.min(1, 300 / img.naturalWidth);
                const lienzo = document.createElement("canvas");
                lienzo.width = Math.max(1, Math.round(img.naturalWidth * escala));
                lienzo.height = Math.max(1, Math.round(img.naturalHeight * escala));
                const ctx = lienzo.getContext("2d");
                ctx.drawImage(img, 0, 0, lienzo.width, lienzo.height);
                const pixeles = ctx.getImageData(0, 0, lienzo.width, lienzo.height).data;

                let transparentes = 0;
                for (let i = 3; i < pixeles.length; i += 4) {
                    if (pixeles[i] < 250) transparentes++;
                }
                // Una firma bien recortada deja transparente la mayor parte del lienzo.
                this.sinTransparencia = transparentes / (pixeles.length / 4) < 0.05;
            } catch (e) {
                this.sinTransparencia = false;
            }
        },
        // ----- Vista sobre una plantilla real -----
        verEnCertificado() {
            this.generando = true;
            const formData = this.datosFormulario();
            formData.append("plantilla_certificado_id", this.plantillaId);

            window.axios.post('/personas-administrativas/vista_previa', formData, {responseType: 'blob'}).then(async response => {
                // Los errores llegan como JSON dentro del blob.
                if ((response.data.type || "").includes("json")) {
                    const cuerpo = JSON.parse(await response.data.text());
                    this.$toast.error(cuerpo.message || "No se pudo generar la vista previa.", {timeout: 12000});
                    return;
                }

                if (this.documento) URL.revokeObjectURL(this.documento);
                this.documento = URL.createObjectURL(response.data);
                this.dialogoDocumento = true;
            })
                .catch(async error => {
                    let mensaje = "No se pudo generar la vista previa.";
                    const datos = error.response && error.response.data;
                    if (datos && datos.text) {
                        try {
                            const cuerpo = JSON.parse(await datos.text());
                            mensaje = (cuerpo.data && typeof cuerpo.data === "object" && !Array.isArray(cuerpo.data)
                                ? Object.values(cuerpo.data).flat().join(" ") : "") || cuerpo.message || mensaje;
                        } catch (e) { /* respuesta no legible */ }
                    }
                    this.$toast.error(mensaje, {timeout: 12000});
                })
                .finally(() => {
                    this.generando = false;
                });
        },
        mostrarError(error, mensaje) {
            console.log(error);
            const cuerpo = error.response && error.response.data;
            if (cuerpo && cuerpo.data && typeof cuerpo.data === "object" && !Array.isArray(cuerpo.data)) {
                mensaje = Object.values(cuerpo.data).flat().join(" ") || mensaje;
            } else if (cuerpo && cuerpo.message) {
                mensaje = cuerpo.message;
            }
            this.$toast.error(mensaje, {timeout: 10000});
        },
    },
    created() {
        this.getFirmaCreate();
    },
    beforeDestroy() {
        this.liberarImagenLocal();
        if (this.documento) URL.revokeObjectURL(this.documento);
    },
}
</script>

<style lang="scss" scoped>
.convenios ::v-deep .v-expansion-panel-header {
    min-height: 36px;
}

.vista__titulo {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 0.8rem;
    font-weight: 600;
    color: #1b2340;
    margin-bottom: 6px;
}

.vista__hoja {
    border: 1px solid #dfe3ee;
    border-radius: 6px;
    padding: 18px 24px 16px;
    text-align: center;
    background: #fff;

    &--cuadros {
        background-color: #fff;
        background-image:
            linear-gradient(45deg, #d9dce6 25%, transparent 25%),
            linear-gradient(-45deg, #d9dce6 25%, transparent 25%),
            linear-gradient(45deg, transparent 75%, #d9dce6 75%),
            linear-gradient(-45deg, transparent 75%, #d9dce6 75%);
        background-size: 16px 16px;
        background-position: 0 0, 0 8px, 8px -8px, -8px 0;
    }
}

.vista__firma {
    height: 110px;
    display: flex;
    align-items: flex-end;
    justify-content: center;

    img {
        max-height: 110px;
        max-width: 80%;
        object-fit: contain;
        // La firma se apoya sobre la línea, como en el certificado.
        margin-bottom: -14px;
    }
}

.vista__linea {
    width: 70%;
    margin: 0 auto;
    border-top: 1.5px solid #1b2340;
}

.vista__nombre {
    margin-top: 6px;
    font-weight: 700;
    color: #1b2340;
}

.vista__cargo {
    font-size: 0.8rem;
    color: #5b6380;
}

.documento {
    display: block;
    width: 100%;
    border: 1px solid #dfe3ee;
    border-radius: 4px;
}
</style>
